import { utcToZonedTime } from "date-fns-tz";
import { isAfter, isBefore, setHours, setMinutes } from "date-fns/esm";
import { createSelector } from "reselect";

import Setting from "@mapmycustomers/shared/types/Setting";

import { RootState } from "@app/store/rootReducer";
import { activityLayoutModel } from "@app/util/layout/impl";

import OrganizationSetting from "../../enum/OrganizationSetting";
import doesEmailActivityLayoutHaveRequiredFields from "../../util/email/doesEmailActivityLayoutHaveRequiredFields";
import { getOrganizationSettings, getUserMetaData, getUserSettings } from "../iam";

const ATTACHMENTS_SIZE_LIMIT = 24 * 1024 * 1024;

const emailState = (state: RootState) => state.email;

export const isEmailCreationModalVisible = createSelector(
  emailState,
  ({ creationModalVisible }) => creationModalVisible
);

export const getEmailQuota = createSelector(emailState, ({ emailQuota }) => emailQuota);

export const getEmailSyncProgress = createSelector(
  emailState,
  ({ emailSyncProgress }) => emailSyncProgress
);

export const getEmailRecipients = createSelector(emailState, ({ recipients }) => recipients);

export const getEmailQueue = createSelector(emailState, ({ emailQueue }) => emailQueue);

export const isEmailQueueLoading = createSelector(
  emailState,
  ({ emailQueueLoading }) => emailQueueLoading
);

export const isNewEmailPreviewLoading = createSelector(
  emailState,
  ({ newEmailPreviewLoading }) => newEmailPreviewLoading
);

export const getNewEmailPreview = createSelector(
  emailState,
  ({ newEmailPreview }) => newEmailPreview
);

export const isTemplatePreviewLoading = createSelector(
  emailState,
  ({ templatePreviewLoading }) => templatePreviewLoading
);

export const getTemplatePreview = createSelector(
  emailState,
  ({ templatePreview }) => templatePreview
);

export const getNewEmailAttachments = createSelector(
  emailState,
  ({ newEmailAttachments }) => newEmailAttachments
);

export const getNewEmailAssociatedEntities = createSelector(
  emailState,
  ({ newEmailAssociatedEntities }) => newEmailAssociatedEntities
);

export const getNewEmailDeals = createSelector(emailState, ({ newEmailDeals }) => newEmailDeals);

export const isNewEmailUnsubscribeLinkIncluded = createSelector(
  emailState,
  ({ newEmailUnsubscribeLinkIncluded }) => newEmailUnsubscribeLinkIncluded
);

export const isNewEmailFilesSizeLimitReached = createSelector(
  emailState,
  ({ newEmailAttachments }) =>
    ATTACHMENTS_SIZE_LIMIT <
    newEmailAttachments.reduce<number>(
      (result, attachment) => result + attachment.preparedFile.size,
      0
    )
);

export const getNewEmailRecipientsType = createSelector(
  emailState,
  ({ newEmailRecipientType }) => newEmailRecipientType
);

export const getEmailTemplates = createSelector(
  emailState,
  getNewEmailRecipientsType,
  ({ templates }, entityType) =>
    entityType ? templates.filter((template) => template.entityType === entityType) : []
);

export const areEmailTemplatesLoading = createSelector(
  emailState,
  ({ templatesLoading }) => templatesLoading
);

export const doesNeedToOpenActivityFormAfterSendingEmail = createSelector(
  getUserMetaData,
  (metaData) => {
    const userSettingValue = metaData.openActivityFormAfterSendingEmail;
    return (
      !!userSettingValue ||
      activityLayoutModel.layouts.length > 1 ||
      doesEmailActivityLayoutHaveRequiredFields()
    );
  }
);

export const isNewEmailTemplateCreating = createSelector(
  emailState,
  ({ templateCreating }) => templateCreating
);

export const isEmailTemplateUpdating = createSelector(
  emailState,
  ({ templateUpdating }) => templateUpdating
);

export const isEmailTemplateDeleting = createSelector(
  emailState,
  ({ templateDeleting }) => templateDeleting
);

export const isEmailSending = createSelector(emailState, ({ emailSending }) => emailSending);

export const isEmailQueueVisible = createSelector(
  emailState,
  ({ emailQueueVisible }) => emailQueueVisible
);

export const isEmailFeaturesVisible = createSelector(
  emailState,
  ({ emailFeaturesVisible }) => emailFeaturesVisible
);

export const isOrgReachEmailDayLimit = createSelector(getEmailQuota, (quota) =>
  quota ? quota.total <= quota.sent + quota.submitted : false
);

export const isEmailQuotaUpdating = createSelector(
  emailState,
  ({ emailQuotaUpdating }) => emailQuotaUpdating
);

export const isOutOfEmailSendingHours = createSelector(
  getOrganizationSettings,
  getUserSettings,
  (orgSettings: Setting[] | undefined, userSettings: Setting[] | undefined) => {
    const timezone = orgSettings?.find(({ key }) => key === OrganizationSetting.TIMEZONE)?.value;
    const emailSetting = userSettings?.find(({ key }) => key === "bulkEmail")?.value;
    if (timezone && emailSetting) {
      const startTime = emailSetting.startTime.split(":");
      const endTime = emailSetting.endTime.split(":");
      const startHour = startTime[0];
      const startMinute = startTime[1] ?? 0;
      const endHour = endTime[0];
      const endMinute = endTime[1] ?? 0;
      const dateInTimeZone = utcToZonedTime(new Date(), timezone);

      return (
        isBefore(dateInTimeZone, setMinutes(setHours(dateInTimeZone, startHour), startMinute)) ||
        isAfter(dateInTimeZone, setMinutes(setHours(dateInTimeZone, endHour), endMinute)) ||
        // we consider then excludedDays can contain only weekend
        (Array.isArray(emailSetting.excludedDays) &&
          emailSetting.excludedDays.length &&
          [0, 6].includes(dateInTimeZone.getDay()))
      );
    }
    return false;
  }
);
